import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, MaskLines } from "@/components/site/Reveal";
import { BrandMarquee } from "@/components/site/Marquee";
import { Seal } from "@/components/site/Seal";
import { Seo } from "@/components/site/Seo";
import { IMAGES, SERVICES, WHY_OUTDOOR, INDUSTRIES, WHY_CHOOSE, WHY_TWINS, RAILWAY_NETWORK } from "@/lib/data";

const Overline = ({ children, dark }) => (
  <span className={`inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] ${dark ? "text-gold" : "text-maroon"}`}>
    <span className={`h-px w-8 ${dark ? "bg-gold" : "bg-maroon"}`} />
    {children}
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.62, 0.85]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden flex items-end">
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img src={IMAGES.heroRailway} alt="Busy Indian railway station" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 -z-10 bg-maroon" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/70 via-black/20 to-black/40" />

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 w-full pb-20 md:pb-28 pt-36">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          <Overline dark>India's Trusted Advertising Partner</Overline>
        </motion.div>

        <h1 className="mt-8 font-display text-cream text-[13vw] leading-[0.92] sm:text-7xl md:text-8xl lg:text-[7.5rem] tracking-tight max-w-5xl">
          <MaskLines
            lines={["Make Your Brand", <span key="i" className="italic text-gold">Visible</span>, "Where Millions Travel"]}
            lineClass=""
            start={0.15}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-8 text-cream/85 text-base md:text-lg max-w-2xl leading-relaxed"
        >
          Twins Media specializes in high-impact advertising across railway stations, digital display
          networks, outdoor branding, and strategic public spaces — serving businesses across multiple states in India.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-quote-btn"
            className="group inline-flex items-center gap-3 bg-gold text-maroon px-8 py-4 text-sm uppercase tracking-[0.18em] hover:bg-gold-hover transition-colors duration-300"
          >
            Get a Quote
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-contact-btn"
            className="inline-flex items-center gap-3 border border-cream/50 text-cream px-8 py-4 text-sm uppercase tracking-[0.18em] hover:bg-cream hover:text-maroon transition-colors duration-300"
          >
            Contact Us
          </Link>
        </motion.div>
      </div>

      {/* Gold ribbon */}
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, delay: 1 }}
        className="hidden lg:flex flex-col items-center absolute right-10 bottom-28 gap-4"
      >
        <Seal className="w-24 h-24" />
        <div className="bg-gold text-maroon px-5 py-2 text-xs uppercase tracking-[0.25em] font-medium">
          The Railway Advertiser
        </div>
      </motion.div>
    </section>
  );
};

const AboutTeaser = () => (
  <section data-testid="about-teaser" className="py-24 md:py-36 bg-cream">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
      <div className="lg:col-span-5">
        <Reveal><Overline>About Twins Media</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[1.02] tracking-tight text-charcoal">
            A Legacy in <span className="italic text-maroon">Transit Media</span>
          </h2>
        </Reveal>
      </div>
      <div className="lg:col-span-7 space-y-6 text-charcoal-muted text-lg leading-relaxed">
        <Reveal delay={0.1}>
          <p>Since <span className="text-maroon font-medium">1997</span>, Twins Media has been helping businesses build stronger brands through innovative advertising solutions across railway stations and high-footfall public locations.</p>
        </Reveal>
        <Reveal delay={0.15}>
          <p>Our focus is simple — connect brands with people through strategic advertising placements that maximize visibility and customer engagement.</p>
        </Reveal>
        <Reveal delay={0.2}>
          <p>Whether you're promoting a local business or launching a nationwide campaign, our experienced team provides end-to-end support from planning and design to installation and campaign execution.</p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 pt-8 border-t border-charcoal/10">
            {WHY_TWINS.map((w) => (
              <div key={w} className="flex items-start gap-3 text-base text-charcoal">
                <span className="text-gold mt-1">✦</span>{w}
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesOverview = () => (
  <section data-testid="services-overview" className="py-24 md:py-32 bg-paper">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
        <div>
          <Reveal><Overline>Our Services</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight text-charcoal">What We Do Best</h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <Link to="/services" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-maroon hover:gap-4 transition-all duration-300">
            All Services <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10">
        {SERVICES.map((s) => (
          <Reveal key={s.id}>
            <Link to="/services" className="group relative block bg-cream p-8 md:p-10 h-full overflow-hidden">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <img src={s.image} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-maroon/80" />
              </div>
              <div className="relative z-10">
                <span className="font-display text-5xl text-gold group-hover:text-gold transition-colors">{s.no}</span>
                <h3 className="mt-6 font-display text-3xl text-charcoal group-hover:text-cream transition-colors duration-500">{s.title}</h3>
                <p className="mt-4 text-charcoal-muted group-hover:text-cream/80 transition-colors duration-500 text-[15px] leading-relaxed">{s.desc}</p>
                <ArrowUpRight className="mt-6 text-maroon group-hover:text-gold transition-colors duration-500" />
              </div>
            </Link>
          </Reveal>
        ))}
        <Reveal>
          <Link to="/services" className="group relative flex flex-col items-start justify-center bg-maroon p-8 md:p-10 h-full min-h-[220px] overflow-hidden">
            <h3 className="font-display text-3xl text-cream leading-snug">Explore Every <span className="italic text-gold">Format</span></h3>
            <p className="mt-4 text-cream/70 text-[15px] leading-relaxed">See the full range of railway, digital, outdoor and transit advertising solutions we offer.</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-gold group-hover:gap-4 transition-all duration-300">
              All Services <ArrowUpRight size={16} />
            </span>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const WhyOutdoor = () => (
  <section data-testid="why-outdoor" className="py-24 md:py-36 bg-maroon text-cream relative overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-16">
      <div className="lg:col-span-5">
        <Reveal><Overline dark>Why Outdoor Advertising?</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
            Seen in the <span className="italic text-gold">real world</span>, every single day.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 text-cream/75 text-lg leading-relaxed">
            Outdoor advertising continues to be one of the most effective ways to build brand awareness and reach
            customers in the real world. With consistent visibility and repeated exposure, your message stays in front
            of potential customers throughout their daily journey.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-7 lg:pl-10">
        <div className="divide-y divide-cream/15 border-t border-cream/15">
          {WHY_OUTDOOR.map((b, i) => (
            <Reveal key={b} delay={i * 0.05}>
              <div className="group flex items-center gap-6 py-6">
                <span className="font-display text-3xl text-gold w-16">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-2xl md:text-3xl font-display group-hover:translate-x-2 transition-transform duration-300">{b}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const IndustriesGrid = () => (
  <section data-testid="industries-grid" className="py-24 md:py-32 bg-cream">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <div className="mb-14 max-w-2xl">
        <Reveal><Overline>Industries We Serve</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight text-charcoal">Trusted Across Sectors</h2>
        </Reveal>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-charcoal/10">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind} delay={(i % 4) * 0.04}>
            <div className="group bg-cream p-8 h-full min-h-[130px] flex flex-col justify-between hover:bg-maroon transition-colors duration-400 cursor-default">
              <span className="text-xs text-gold tracking-widest">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-display text-2xl text-charcoal group-hover:text-cream transition-colors duration-400 mt-6">{ind}</h3>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyChoose = () => (
  <section data-testid="why-choose" className="py-24 md:py-32 bg-paper">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <div className="mb-16 max-w-2xl">
        <Reveal><Overline>Why Clients Choose Us</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight text-charcoal">Built on <span className="italic text-maroon">Trust</span></h2>
        </Reveal>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {WHY_CHOOSE.map((w) => (
          <Reveal key={w.no}>
            <div className="group border-t-2 border-maroon pt-6 h-full">
              <span className="font-display text-6xl text-gold/60 group-hover:text-gold transition-colors duration-300">{w.no}</span>
              <h3 className="mt-4 font-display text-2xl text-charcoal leading-snug">{w.title}</h3>
              <p className="mt-3 text-charcoal-muted text-[15px] leading-relaxed">{w.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Coverage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const stationCount = RAILWAY_NETWORK.reduce((sum, s) => sum + s.stations.length, 0);
  const stateCount = RAILWAY_NETWORK.length;
  return (
    <section ref={ref} data-testid="coverage-section" className="relative py-28 md:py-40 overflow-hidden bg-charcoal">
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <img src={IMAGES.outdoorHoarding} alt="" className="w-full h-full object-cover scale-125" />
        <div className="absolute inset-0 bg-charcoal/80" />
      </motion.div>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 text-cream">
          <Reveal><Overline dark>Our Network</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display text-5xl md:text-7xl leading-[1.02] tracking-tight">
              A Multi-State <span className="italic text-gold">Railway Network</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-cream/75 text-lg leading-relaxed max-w-xl">
              Twins Media operates a wide-ranging railway advertising network across {stateCount} states in South
              India — Tamil Nadu, Karnataka, Telangana and Maharashtra — with premium opportunities at high-footfall
              stations and strategic railway locations.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[["1997", "Established"], [`${stationCount}+`, "Rail Locations"], [String(stateCount), "States Covered"]].map(([n, l]) => (
                <div key={l} className="border-l border-gold/40 pl-4">
                  <div className="font-display text-4xl text-gold">{n}</div>
                  <div className="text-xs uppercase tracking-widest text-cream/60 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/network" data-testid="coverage-network-link" className="mt-10 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-gold hover:gap-4 transition-all duration-300">
              View Full Network <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.1}>
            <div className="border border-gold/30 p-3 bg-cream/5 backdrop-blur-sm">
              <img src={IMAGES.coverageMap} alt="Twins Media railway advertising network coverage map" className="w-full h-auto" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const CTA = () => (
  <section data-testid="cta-section" className="py-24 md:py-36 bg-cream">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <Reveal>
        <div className="bg-maroon text-cream px-8 md:px-20 py-20 md:py-28 text-center relative overflow-hidden">
          <div className="absolute -left-16 -top-16 w-64 h-64 opacity-[0.07]"><Seal className="w-full h-full" /></div>
          <Overline dark>Ready to Promote Your Business?</Overline>
          <h2 className="mt-6 font-display text-5xl md:text-7xl leading-[1.03] tracking-tight max-w-4xl mx-auto">
            Let us create a campaign that delivers <span className="italic text-gold">visibility & results</span>.
          </h2>
          <Link
            to="/contact"
            data-testid="cta-quote-btn"
            className="mt-10 inline-flex items-center gap-3 bg-gold text-maroon px-10 py-4 text-sm uppercase tracking-[0.18em] hover:bg-gold-hover transition-colors duration-300"
          >
            Request a Quote <ArrowUpRight size={18} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo title="Railway & Outdoor Advertising Since 1997" description="Twins Media specializes in high-impact railway station advertising, digital display screens, outdoor hoardings, transit media and brand promotion campaigns across India." path="/" />
      <Hero />
      <BrandMarquee />
      <AboutTeaser />
      <ServicesOverview />
      <WhyOutdoor />
      <IndustriesGrid />
      <WhyChoose />
      <Coverage />
      <CTA />
    </div>
  );
}
